import axios from 'axios';
import { getIdentity } from './identityService';

// Use empty string fallback for SWA emulator mode (relative paths go through SWA proxy)
const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({ baseURL: API_URL });

// Request interceptor to add demo token if present
api.interceptors.request.use(
  (config) => {
    const demoToken = sessionStorage.getItem('demoToken');
    if (demoToken) {
      config.headers['Authorization'] = `Bearer ${demoToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response interceptor for automatic logout on 401 (session timeout/token expiry)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if demo session expired
      const demoSession = sessionStorage.getItem('demoSession');
      if (demoSession) {
        console.warn('Demo session expired (401). Redirecting to login...');
        sessionStorage.removeItem('demoToken');
        sessionStorage.removeItem('demoSession');
        window.location.href = '/';
      } else {
        console.warn('Session expired (401). Redirecting to login...');
        window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
      }
      return new Promise(() => {}); // Never resolves - page is redirecting
    }
    return Promise.reject(error);
  }
);

const withUserHeaders = async () => {
  const id = await getIdentity();
  if (!id?.user_id) throw new Error('User not authenticated');
  // Backend resolves user from headers; identity mapping is already server-side.
  return { headers: { 'x-user-email': id.email || '' } };
};

export const getAnalyticsFilters = async () => {
  try {
    const cfg = await withUserHeaders();
    const res = await api.get('/api/analytics/filters', cfg);
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

export const getOrganizations = async () => {
  try {
    const cfg = await withUserHeaders();
    const res = await api.get('/api/admin/organizations', cfg);
    // Transform to return both id and name for frontend compatibility
    return res.data.map(org => ({
      id: org.id,
      name: org.name,
      domains: org.domains || []
    }));
  } catch (err) {
    console.error('Failed to fetch organizations:', err);
    // Return empty array on error to prevent dropdown issues
    return [];
  }
};

export const getPersonalAnalytics = async (params) => {
  try {
    const cfg = await withUserHeaders();
    const res = await api.get('/api/analytics/personal', { ...cfg, params });
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

export const getOrganizationalAnalytics = async (orgId, params) => {
  try {
    const cfg = await withUserHeaders();
    const res = await api.get('/api/analytics/organization', { ...cfg, params: { org_id: orgId, include_ai: params?.include_ai ?? false, ...params } });
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

export const getOrganizationalPriorityInsights = async (orgId, params) => {
  try {
    const cfg = await withUserHeaders();
    const res = await api.get('/api/analytics/organization/priority-insights', {
      ...cfg,
      params: { org_id: orgId, include_ai: params?.include_ai ?? false, ...params }
    });
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

export const generateOrganizationalPriorityInsights = async (orgId, params) => {
  try {
    const cfg = await withUserHeaders();
    const res = await api.get('/api/analytics/organization/priority-insights', {
      ...cfg,
      params: { org_id: orgId, include_ai: true, ...params }
    });
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

export const getIndustryAnalytics = async (industry, params) => {
  try {
    const cfg = await withUserHeaders();
    const res = await api.get('/api/analytics/industry', { ...cfg, params: { industry, include_ai: params?.include_ai ?? false, ...params } });
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

export const generateIndustryPriorityInsights = async (industry, params) => {
  try {
    const cfg = await withUserHeaders();
    const res = await api.get('/api/analytics/industry/priority-insights', {
      ...cfg,
      params: { industry, include_ai: true, ...params }
    });
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * Generate goal-aligned report for organization.
 *
 * @param {string} orgId - Organization ID
 * @param {object} params - Parameters including:
 *   - period: Time period (e.g., '3m') (optional)
 *   - start_date: Custom start date (optional)
 *   - end_date: Custom end date (optional)
 *   - force_regenerate: Skip cache (optional, default: false)
 * @returns {Promise<object>} Complete goal-aligned report with all sections
 */
export const generateGoalReport = async (orgId, params) => {
  try {
    const cfg = await withUserHeaders();
    const requestBody = {
      org_id: orgId,
      force_regenerate: params.force_regenerate ?? false,
      period: params.period ?? '3m',
      start_date: params.start_date || null,
      end_date: params.end_date || null
    };

    const res = await api.post('/api/analytics/reports/generate', requestBody, cfg);
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * Retrieve a previously generated goal-aligned report by ID.
 *
 * @param {string} reportId - Report ID
 * @returns {Promise<object>} Goal-aligned report
 */
export const getGoalReport = async (reportId) => {
  try {
    const cfg = await withUserHeaders();
    const res = await api.get(`/api/analytics/reports/${reportId}`, cfg);
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * List goal-aligned reports for an organization.
 *
 * @param {string} orgId - Organization ID
 * @param {number} limit - Maximum number of reports to return (default: 10)
 * @returns {Promise<Array>} List of report metadata
 */
export const listGoalReports = async (orgId, limit = 10) => {
  try {
    const cfg = await withUserHeaders();
    const res = await api.get('/api/analytics/reports/list', {
      ...cfg,
      params: { org_id: orgId, limit }
    });
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * Validate analytics question using Guard agent (Phase 1 of two-phase Q&A).
 * Fast validation (~500-1000ms) that returns enhanced_question or rejection.
 *
 * @param {string} question - User's analytics question (max 400 chars)
 * @param {string} orgId - Organization ID to query
 * @returns {Promise<object>} Validation result with:
 *   - valid: boolean
 *   - enhanced_question: string (if valid)
 *   - rejection_reason: string (if invalid)
 *   - suggested_question: string (if invalid)
 *   - category: string
 *   - confidence: number (0.0-1.0)
 * @throws {object} Error with status, code, message
 */
export const validateQuestion = async (question, orgId) => {
  try {
    const cfg = await withUserHeaders();
    const requestBody = {
      question,
      org_id: orgId
    };

    const res = await api.post('/api/analytics/ask-validate', requestBody, cfg);
    return res.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * Analyze validated question and generate answer (Phase 2 of two-phase Q&A).
 * Assumes question already validated by validateQuestion().
 *
 * @param {string} question - Original user question
 * @param {string} enhancedQuestion - Enhanced question from validation step
 * @param {string} orgId - Organization ID to query
 * @param {string} [category] - Question category from validation (default: 'general')
 * @param {number} [confidence] - Confidence score from validation (default: 0.8)
 * @returns {Promise<object>} Answer with metadata including:
 *   - answer: Markdown-formatted answer
 *   - question_original: Original question
 *   - question_enhanced: Enhanced question used for analysis
 *   - cached: Whether answer was cached
 *   - category: Question category
 *   - timestamp: Response timestamp
 * @throws {object} Error with:
 *   - status: HTTP status code
 *   - code: Error code (RATE_LIMIT_EXCEEDED, etc.)
 *   - message: Error message
 */
export const analyzeQuestion = async (question, enhancedQuestion, orgId, category = 'general', confidence = 0.8) => {
  try {
    const cfg = await withUserHeaders();
    const requestBody = {
      question,
      enhanced_question: enhancedQuestion,
      org_id: orgId,
      category,
      confidence
    };

    const res = await api.post('/api/analytics/ask-analyze', requestBody, cfg);
    return res.data;
  } catch (err) {
    // Handle rate limit errors
    if (err?.response?.status === 429) {
      const retryAfter = err.response.headers['retry-after'];
      return Promise.reject({
        status: 429,
        code: 'RATE_LIMIT_EXCEEDED',
        message: err?.response?.data?.detail || 'Rate limit exceeded',
        retry_after: retryAfter ? parseInt(retryAfter, 10) : null
      });
    }

    throw normalizeError(err);
  }
};

function normalizeError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail || err?.message || 'Request failed';
  let code = 'UNKNOWN';
  if (status === 422) code = 'INVALID_DATES';
  else if (status === 403) code = 'FORBIDDEN';
  else if (status === 401) code = 'UNAUTHORIZED';
  else if (status === 501) code = 'COSMOS_DISABLED';
  return { status, code, message: detail };
}
